import { useAddSnackbarMessage } from '@shared/useAddSnackBar';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getWorkScheduleMonth, updateWorkScheduleMonth, copyFromPreviousMonth } from './api';
import dayjs, { Dayjs } from 'dayjs';
import {
  DEFAULT_WEEKLY_SCHEDULE,
  DaySchedule,
  Holiday,
  MonthSchedule,
  WeeklySchedule,
  WorkingHoursScheduleType,
  WorkScheduleMonth,
} from '../../visits/schedule/entity';
import { useAnalytics } from '@libs/mixpanel/useAnalytics';

export const useWorkScheduleQuery = (year: number, month: number) => {
  return useQuery<WorkScheduleMonth>({
    queryKey: ['workSchedule', year, month],
    queryFn: () => getWorkScheduleMonth(year, month),
    enabled: !isNaN(year) && !isNaN(month),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useUpdateWorkScheduleMutation = () => {
  const notify = useAddSnackbarMessage();
  const { t } = useIntlTranslations();
  const queryClient = useQueryClient();
  const { trackOnboardingStep } = useAnalytics();

  return useMutation({
    mutationKey: ['saveWorkSchedule'],
    onError: () => notify({ text: t('workSchedule.saveFailed'), type: 'error' }),
    mutationFn: ({
      year,
      month,
      schedule,
    }: {
      year: number;
      month: number;
      schedule: MonthSchedule;
    }) => updateWorkScheduleMonth(year, month, schedule),
    onSuccess: async data => {
      notify({ text: t('workSchedule.saved'), type: 'success' });
      trackOnboardingStep('workScheduleConfigured', 2);
      const from = dayjs(`${data.year}-${String(data.month).padStart(2, '0')}-01`);
      const to = from.endOf('month');

      queryClient.invalidateQueries({
        queryKey: ['visits', from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD')],
      });

      await queryClient.invalidateQueries({ queryKey: ['workSchedule'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
      queryClient.invalidateQueries({ queryKey: ['customerAvailableSlots'] });
      queryClient.invalidateQueries({ queryKey: ['highlightedDays'] });
      queryClient.invalidateQueries({ queryKey: ['publicHighlightedDays'] });
      queryClient.invalidateQueries({ queryKey: ['publicSlots'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
    },
  });
};

export const useCopyFromPreviousMonthMutation = () => {
  const notify = useAddSnackbarMessage();
  const { t } = useIntlTranslations();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['saveWorkSchedule'],
    onError: () => notify({ text: t('workSchedule.saveFailed'), type: 'error' }),
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      copyFromPreviousMonth(year, month),
    onSuccess: async () => {
      notify({ text: t('workSchedule.saved'), type: 'success' });
      await queryClient.invalidateQueries({ queryKey: ['workSchedule'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
      queryClient.invalidateQueries({ queryKey: ['customerAvailableSlots'] });
      queryClient.invalidateQueries({ queryKey: ['highlightedDays'] });
      queryClient.invalidateQueries({ queryKey: ['publicHighlightedDays'] });
      queryClient.invalidateQueries({ queryKey: ['publicSlots'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-progress'] });
    },
  });
};

const DAY_OF_WEEK_MAP: Record<number, keyof WeeklySchedule> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

export const useDayScheduleEdit = (selectedDate: Dayjs | null) => {
  const year = selectedDate?.year() ?? dayjs().year();
  const month = (selectedDate?.month() ?? dayjs().month()) + 1;

  const { data: workSchedule } = useWorkScheduleQuery(year, month);
  const { mutate: updateSchedule } = useUpdateWorkScheduleMutation();

  const defaultSchedule: WorkingHoursScheduleType = {
    weeklySchedule: workSchedule?.schedule?.weeklySchedule ?? DEFAULT_WEEKLY_SCHEDULE,
    holidays: workSchedule?.schedule?.holidays ?? [],
    dayOverrides: workSchedule?.schedule?.dayOverrides ?? {},
  };

  const dateStr = selectedDate?.format('YYYY-MM-DD') ?? '';
  const currentHoliday = defaultSchedule.holidays.find(h => h.date === dateStr);
  const currentDayOverride = defaultSchedule.dayOverrides?.[dateStr];

  const defaultDaySchedule: DaySchedule = selectedDate
    ? (defaultSchedule.weeklySchedule[DAY_OF_WEEK_MAP[selectedDate.day()]] ?? {
        isWorking: false,
        timeSlots: [],
      })
    : { isWorking: false, timeSlots: [] };

  const handleDaySave = (holiday?: Holiday, dayOverride?: DaySchedule) => {
    if (!selectedDate) {
      return;
    }

    const updatedSchedule = { ...defaultSchedule };
    updatedSchedule.holidays = updatedSchedule.holidays.filter(h => h.date !== dateStr);
    const overrides = { ...(updatedSchedule.dayOverrides ?? {}) };
    delete overrides[dateStr];

    if (holiday) {
      updatedSchedule.holidays = [...updatedSchedule.holidays, holiday];
    } else if (dayOverride) {
      overrides[dateStr] = dayOverride;
    }

    updatedSchedule.dayOverrides = overrides;
    updateSchedule({ year, month, schedule: updatedSchedule });
  };

  return { defaultSchedule, currentHoliday, currentDayOverride, defaultDaySchedule, handleDaySave };
};
